import {
  Eye,
  EyeOff,
  Lock,
  Mail,
  Phone,
  Scale,
  User,
  Users,
} from "lucide-react";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import { useAuth } from "../../context/AuthProvider";
import logger from "../../utils/logger";
import flag from "../../assets/in.png";

interface SignupFormData {
  firstName: string;
  lastName: string;
  username: string;
  email: string;
  phone: string;
  dialCode: string;
  role: string;
  // phoneCode: string;
  enrollment?: string;
  password: string;
  confirmPassword: string;
  terms: boolean;
}

const isValidPhone = (phone: string) => {
  if (!/^[6-9]\d{9}$/.test(phone)) {
    return "Please enter a valid phone number";
  }

  if (/^(\d)\1{9}$/.test(phone)) {
    return "Phone number cannot contain all identical digits";
  }

  return true;
};

const Signup = () => {
  const navigate = useNavigate();
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const { signUpUser } = useAuth();

  const {
    register,
    handleSubmit,
    watch,
    setError,
    clearErrors,
    setValue,
    formState: { errors, isSubmitting: loading },
  } = useForm<SignupFormData>({
    mode: "onChange",
    defaultValues: {
      firstName: "",
      lastName: "",
      username: "",
      email: "",
      phone: "",
      dialCode: "+91",
      role: "",
      enrollment: "",
      password: "",
      confirmPassword: "",
    },
  });

  const passwordValue = watch("password");
  const roleValue = watch("role");

  const onSubmit = async (data: SignupFormData) => {
    try {
      await signUpUser(data);
    } catch (error: any) {
      logger.log("page-error", error);
    }
  };

  const validatePassword = (password: string) => {
    const minLength = password.length >= 8;
    const hasUpper = /[A-Z]/.test(password);
    const hasLower = /[a-z]/.test(password);
    const hasNumber = /\d/.test(password);
    const hasSpecial = /[!@#$%^&*(),.?":{}|<>]/.test(password);
    return minLength && hasUpper && hasLower && hasNumber && hasSpecial;
  };

  return (
    <div className="w-full max-w-md mx-auto">
      <div className="text-center mb-8">
        <div className="bg-white rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
          {/* <Scale className="w-8 h-8 text-white" /> */}
          <img src="logo.png" alt="NyayNet Logo" className="w-10 h-10 " />
        </div>
        <h1 className="text-3xl font-bold text-white mb-2">Join NyayNet</h1>
        <p className="text-blue-100">
          Create your account to access legal services
        </p>
      </div>

      <form
        onSubmit={handleSubmit(onSubmit)}
        className="space-y-4"
        autoComplete="off"
      >
        {/* First & Last Name */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <p className="block text-sm font-medium text-blue-100 mb-2">
              First Name *
            </p>
            <div className="relative">
              <User className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-blue-300" />
              <input
                type="text"
                {...register("firstName", {
                  required: "First name is required",
                })}
                onChange={(e) => {
                  let value = e.target.value;

                  if (value.length > 50) {
                    value = value.slice(0, 50);

                    setError("firstName", {
                      type: "manual",
                      message: "First name cannot exceed 50 characters.",
                    });
                  } else {
                    clearErrors("firstName");
                  }

                  setValue("firstName", value);
                }}
                className={`w-full pl-10 pr-4 py-3 bg-white/10 border rounded-lg text-white ${
                  errors.firstName ? "border-red-400" : "border-white/20"
                }`}
                placeholder="First name"
              />

              {errors.firstName && (
                <p className="mt-1 text-sm text-red-300">
                  {errors.firstName.message}
                </p>
              )}
            </div>
          </div>

          <div>
            <p className="block text-sm font-medium text-blue-100 mb-2">
              Last Name *
            </p>
            <div className="relative">
              <User className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-blue-300" />
              <input
                type="text"
                {...register("lastName", {
                  required: "Last name is required",
                })}
                onChange={(e) => {
                  let value = e.target.value;

                  if (value.length > 50) {
                    value = value.slice(0, 50);

                    setError("lastName", {
                      type: "manual",
                      message: "Last name cannot exceed 50 characters.",
                    });
                  } else {
                    clearErrors("lastName");
                  }

                  setValue("lastName", value);
                }}
                className={`w-full pl-10 pr-4 py-3 bg-white/10 border rounded-lg text-white placeholder-blue-200 focus:outline-none ${
                  errors.lastName ? "border-red-400" : "border-white/20"
                }`}
                placeholder="Last name"
              />
            </div>

            {errors.lastName && (
              <p className="mt-1 text-sm text-red-300">
                {errors.lastName.message}
              </p>
            )}
          </div>
        </div>

        {/* Username */}
        <div>
          <p className="block text-sm font-medium text-blue-100 mb-2">
            Username *
          </p>
          <div className="relative">
            <User className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-blue-300" />
            <input
              type="text"
              autoComplete="off"
              {...register("username", {
                required: "Username is required",
                pattern: {
                  value: /^\w{3,20}$/,
                  message:
                    "Please use 3–20 characters. Only letters, numbers, and underscores are allowed.",
                },
              })}
              className={`w-full pl-10 pr-4 py-3 bg-white/10 border rounded-lg text-white ${
                errors.username ? "border-red-400" : "border-white/20"
              }`}
              placeholder="Choose a username"
              onInput={(e) => {
                const input = e.target as HTMLInputElement;
                input.value = input.value
                  .replace(/\s/g, "")
                  .toLocaleLowerCase();
              }}
            />
          </div>
          {errors.username && (
            <p className="mt-1 text-sm text-red-300">
              {errors.username.message}
            </p>
          )}
        </div>

        {/* Email */}
        <div>
          <p className="block text-sm font-medium text-blue-100 mb-2">
            Email Address *
          </p>
          <div className="relative">
            <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-blue-300" />
            <input
              type="email"
              autoComplete="off"
              {...register("email", {
                required: "Email address is required",
                pattern: {
                  value:
                    /^(?!\.)(?!.*\.\.)[a-zA-Z0-9._%+-]+(?<!\.)@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
                  message: "Please enter a valid email address",
                },
              })}
              className={`w-full pl-10 pr-4 py-3 bg-white/10 border rounded-lg text-white ${
                errors.email ? "border-red-400" : "border-white/20"
              }`}
              placeholder="your@email.com"
            />
          </div>
          {errors.email && (
            <p className="mt-1 text-sm text-red-300">{errors.email.message}</p>
          )}
        </div>

        {/* Phone */}
        <div className="grid grid-cols-3 gap-2">
          <div>
            <p className="block text-sm font-medium text-blue-100 mb-2">Code</p>

            <input type="hidden" value="+91" {...register("dialCode")} />
            <div className="flex items-center gap-2 px-3 py-3 bg-white/10 border border-white/20 rounded-lg cursor-not-allowed">
              <img
                src={flag}
                srcSet="https://flagcdn.com/w40/in.png 2x"
                width="20"
                height="15"
                alt="India flag"
                className=" object-cover"
              />
              <span className="text-white text-sm font-medium">IN</span>
              <span className="text-blue-200 text-sm">+91</span>
            </div>
          </div>

          <div className="col-span-2">
            <p className="block text-sm font-medium text-blue-100 mb-2">
              Phone Number *
            </p>
            <div className="relative">
              <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-blue-300" />
              <input
                type="tel"
                {...register("phone", {
                  required: "Phone number is required",
                  validate: isValidPhone,
                })}
                onInput={(e) => {
                  const input = e.target as HTMLInputElement;
                  input.value = input.value.replace(/\D/g, "").slice(0, 10);
                }}
                className={`w-full pl-10 pr-4 py-3 bg-white/10 border rounded-lg text-white ${
                  errors.phone ? "border-red-400" : "border-white/20"
                }`}
                placeholder="Enter phone number"
              />
            </div>
            {errors.phone && (
              <p className="mt-1 text-sm text-red-300">
                {errors.phone.message}
              </p>
            )}
          </div>
        </div>

        {/* Role */}
        <div>
          <p className="block text-sm font-medium text-blue-100 mb-2">Role *</p>
          <div className="relative">
            <Users className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-blue-300" />
            <select
              {...register("role", { required: "Please select your role" })}
              className={`w-full pl-10 pr-4 py-3 bg-white/10 border rounded-lg text-white ${
                errors.role ? "border-red-400" : "border-white/20"
              }`}
            >
              <option value="" className="text-black">
                Select your role
              </option>
              <option value="Advocate" className="text-black">
                Advocate
              </option>
              <option value="Individual" className="text-black">
                Individual
              </option>
            </select>
          </div>
          {errors.role && (
            <p className="mt-1 text-sm text-red-300">{errors.role.message}</p>
          )}
        </div>

        {/* Enrollment if Advocate */}
        {roleValue === "Advocate" && (
          <div>
            <p className="block text-sm font-medium text-blue-100 mb-2">
              Bar Council Enrollment Number *
            </p>
            <div className="relative">
              <Scale className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-blue-300" />
              <input
                type="text"
                {...register("enrollment", {
                  required: "Enrollment number is required for advocates",
                  pattern: {
                    value: /^[A-Z]{2}\/\d{4}\/\d{1,10}$/,
                    message:
                      "Enter a valid enrollment number (e.g. MH/2024/123456)",
                  },
                })}
                onInput={(e) => {
                  const input = e.target as HTMLInputElement;
                  input.value = input.value.toUpperCase();
                }}
                className={`w-full pl-10 pr-4 py-3 bg-white/10 border rounded-lg text-white ${
                  errors.enrollment ? "border-red-400" : "border-white/20"
                }`}
                placeholder="MH/2024/123456"
              />
            </div>
            {errors.enrollment && (
              <p className="mt-1 text-sm text-red-300">
                {errors.enrollment.message}
              </p>
            )}
          </div>
        )}

        {/* Password */}
        <div>
          <p className="block text-sm font-medium text-blue-100 mb-2">
            Password *
          </p>
          <div className="relative">
            <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-blue-300" />
            <input
              type={showPassword ? "text" : "password"}
              autoComplete="new-password"
              {...register("password", {
                required: "Password is required",
                validate: (val) => {
                  if (/^\s/.test(val)) {
                    return "Password cannot start with a space";
                  }
                  return (
                    validatePassword(val) ||
                    "Password does not meet requirements"
                  );
                },
              })}
              onInput={(e) => {
                const input = e.target as HTMLInputElement;
                input.value = input.value.replace(/^\s+/, "");
              }}
              className={`w-full pl-10 pr-12 py-3 bg-white/10 border rounded-lg text-white ${
                errors.password ? "border-red-400" : "border-white/20"
              }`}
              placeholder="Create a strong password"
            />

            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-3 top-1/2 transform -translate-y-1/2 text-blue-300"
            >
              {showPassword ? (
                <Eye className="w-5 h-5" />
              ) : (
                <EyeOff className="w-5 h-5" />
              )}
            </button>
          </div>
          {errors.password && (
            <p className="mt-1 text-sm text-red-300">
              {errors.password.message}
            </p>
          )}
        </div>

        {/* Confirm Password */}
        <div>
          <p className="block text-sm font-medium text-blue-100 mb-2">
            Confirm Password *
          </p>
          <div className="relative">
            <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-blue-300" />
            <input
              type={showConfirmPassword ? "text" : "password"}
              autoComplete="new-password"
              {...register("confirmPassword", {
                required: "Please confirm your password",
                validate: (val) => {
                  if (/^\s/.test(val)) {
                    return "Password cannot start with a space";
                  }

                  return val === passwordValue || "Passwords do not match";
                },
              })}
              onInput={(e) => {
                const input = e.target as HTMLInputElement;
                input.value = input.value.replace(/^\s+/, "");
              }}
              className={`w-full pl-10 pr-12 py-3 bg-white/10 border rounded-lg text-white ${
                errors.confirmPassword ? "border-red-400" : "border-white/20"
              }`}
              placeholder="Confirm your password"
            />
            <button
              type="button"
              onClick={() => setShowConfirmPassword(!showConfirmPassword)}
              className="absolute right-3 top-1/2 transform -translate-y-1/2 text-blue-300"
            >
              {showConfirmPassword ? (
                <Eye className="w-5 h-5" />
              ) : (
                <EyeOff className="w-5 h-5" />
              )}
            </button>
          </div>
          {errors.confirmPassword && (
            <p className="mt-1 text-sm text-red-300">
              {errors.confirmPassword.message}
            </p>
          )}
        </div>

        <div className="flex items-start space-x-2">
          <input
            type="checkbox"
            id="terms"
            className="mt-1 w-4 h-4 text-blue-600 bg-white/10 border-white/20 rounded"
            // required
            {...register("terms", { required: "Please agree to the terms" })}
          />
          <label htmlFor="terms" className="text-sm text-blue-100">
            I agree to the{" "}
          </label>
          <div className="text-sm text-blue-100">
            <span className="text-white underline">Terms of Service</span> and{" "}
            <span className="text-white underline">Privacy Policy</span>
          </div>
        </div>
        {errors.terms && (
          <p className="text-sm text-red-300">{errors.terms.message}</p>
        )}

        <button
          type="submit"
          className="w-full bg-white text-slate-800 font-semibold py-3 px-6 rounded-lg hover:bg-blue-50 transition-all"
          disabled={loading}
        >
          {loading ? "Creating account..." : "Create Account"}
        </button>

        <div className="text-center">
          <p className="text-blue-100">
            Already have an account?{" "}
            <button
              type="button"
              onClick={() => navigate("/")}
              className="text-white font-semibold underline hover:text-blue-200"
            >
              Sign In
            </button>
          </p>
        </div>
      </form>
    </div>
  );
};

export default Signup;
